// Which screens exist, what each one's URL is, and how they nest. Shared rather than kept inside
// App because the views themselves need to navigate, and a screen that cannot name where it is
// going has to be handed a callback for every destination.

import type { ReportingScope } from "../types/reporting";
import type { OwnerFilter } from "../types";

/**
 * The filters a drill-down opens the transactions list with; any filter left out is cleared.
 * `owner` and `month` choose what is being looked at rather than narrow it, so they are only
 * changed when given.
 */
export type TransactionFilters = {
  reporting?: ReportingScope;
  date?: string;
  category?: string;
  account?: string;
  query?: string;
  type?: string;
  owner?: OwnerFilter;
  month?: string;
  trip?: string;
};

export type View = "overview" | "insights" | "accounts" | "account-settings" | "profile-settings" | "transactions" | "prices" | "settings" | "categories" | "budgets" | "achievements" | "payees" | "ious" | "bills" | "receipts" | "trips" | "goals" | "more" | "ai" | "backups" | "errors" | "staging-data" | "smart-rules";
export const viewRoutes: Record<View, string> = {
  overview: "/overview",
  insights: "/insights",
  accounts: "/accounts",
  "account-settings": "/manage-accounts",
  "profile-settings": "/manage-profiles",
  prices: "/prices",
  transactions: "/transactions",
  settings: "/settings",
  categories: "/categories",
  budgets: "/budgets",
  achievements: "/achievements",
  payees: "/payees",
  ious: "/ious",
  bills: "/bills",
  receipts: "/receipts",
  trips: "/trips",
  goals: "/goals",
  more: "/more",
  ai: "/ai",
  backups: "/backups",
  errors: "/errors",
  "staging-data": "/staging-data",
  "smart-rules": "/smart-rules",
};
// Views reached through the "More" tab rather than being a top-level destination themselves.
export const moreSubviews: View[] = ["insights", "achievements", "prices", "categories", "budgets", "account-settings", "payees", "ious", "bills", "receipts", "trips", "goals"];
// Views reached through the "Settings" screen rather than being a top-level destination themselves.
export const settingsSubviews: View[] = ["profile-settings", "ai", "backups", "errors", "staging-data", "smart-rules"];
// Views that use the plain "options list" page chrome (no month/owner filters).
export const chromelessViews: View[] = [
  "settings",
  "more",
  ...moreSubviews.filter((view) => view !== "insights"),
  ...settingsSubviews,
];
export const routeView = (path = window.location.pathname): View => {
  const match = (Object.entries(viewRoutes) as [View, string][]).find(
    ([, route]) => path === route,
  );
  return match?.[0] || "overview";
};
